import { memo, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  TimePicker,
} from 'antd';
import { ErrorState, Props } from './types';
import styles from './style.module.css';
import { useOutgoingCallsUploadPopup } from '../../../hooks/features/useOutgoingCallsUploadPopup/useOutgoingCallsUploadPopup';
import FormPopup from '../FormPopup/FormPopup';
import { DownloadOutlined } from '@ant-design/icons';
import { UploadResult } from '../../../store/reducers/OutgoingCallsUploadSlice';
import { UploadResultBanner, UploadStatus } from './UploadResultBanner';
import {
  getCampaignEndDisabledTime,
  isBeforeCampaignEndDate,
  isBeforeMinimumCampaignStartDate,
  isCampaignEndAfterStart,
} from '../../../utils/campaignDateTimeRestrictions';

const NO_CONTRACT_MESSAGE =
  'Загрузка для выбранного сценария недоступна: нет контракта API';

const ACCEPTED_FILE_EXTENSION = '.xlsx';
const ACCEPTED_FILE_REGEX = /\.xlsx$/i;
const FILE_VALIDATION_MESSAGE = 'Поддерживается только формат .xlsx';

const OutgoingCallsUploadPopup = memo(({ onCancel, open }: Props) => {
  const reportData = useOutgoingCallsUploadPopup();
  const [errors, setErrors] = useState<ErrorState>({});
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const isLoading = uploadStatus === 'loading';
  const hasResult =
    uploadStatus === 'success' ||
    uploadStatus === 'warning' ||
    uploadStatus === 'error';

  useEffect(() => {
    if (open) {
      reportData.handleReset();
      setErrors({});
      setUploadStatus('idle');
      setUploadResult(null);
    }
  }, [open]);

  const checkValid = () => {
    let hasError = false;
    const newErrors = {} as ErrorState;

    if (!reportData.taskType) {
      newErrors.taskType = {
        validateStatus: 'error',
        help: 'Выберите сценарий',
      };
      hasError = true;
    }

    if (
      reportData.taskType &&
      (reportData.scenarioGroup === 'dispensarization' ||
        reportData.scenarioGroup === 'form') &&
      !reportData.name.trim()
    ) {
      newErrors.name = {
        validateStatus: 'error',
        help: 'Укажите название кампании',
      };
      hasError = true;
    }

    if (
      reportData.taskType &&
      (reportData.scenarioGroup === 'dispensarization' ||
        reportData.scenarioGroup === 'form') &&
      !reportData.fromDate
    ) {
      newErrors.fromDate = {
        validateStatus: 'error',
        help: 'Укажите дату/время начала',
      };
      hasError = true;
    }

    if (
      reportData.taskType &&
      reportData.scenarioGroup === 'dispensarization' &&
      !reportData.toDate
    ) {
      newErrors.toDate = {
        validateStatus: 'error',
        help: 'Укажите дату/время окончания',
      };
      hasError = true;
    }

    if (
      reportData.taskType &&
      (reportData.scenarioGroup === 'dispensarization' ||
        reportData.scenarioGroup === 'form') &&
      reportData.fromDate &&
      isBeforeMinimumCampaignStartDate(reportData.fromDate)
    ) {
      newErrors.fromDate = {
        validateStatus: 'error',
        help: 'Дата начала не может быть раньше завтрашнего дня',
      };
      hasError = true;
    }

    if (
      reportData.taskType &&
      reportData.scenarioGroup === 'dispensarization' &&
      reportData.toDate &&
      !isCampaignEndAfterStart({
        startDate: reportData.fromDate,
        startTime: reportData.fromTime,
        endDate: reportData.toDate,
        endTime: reportData.toTime,
      })
    ) {
      newErrors.toDate = {
        validateStatus: 'error',
        help: 'Дата/время окончания должны быть позже даты/времени начала',
      };
      hasError = true;
    }

    if (!reportData.fileList || !reportData.fileList.length) {
      newErrors.fileList = {
        validateStatus: 'error',
        help: 'Загрузите файл',
      };
      hasError = true;
    } else if (
      reportData.fileList[0].name &&
      !ACCEPTED_FILE_REGEX.test(reportData.fileList[0].name)
    ) {
      newErrors.fileList = {
        validateStatus: 'error',
        help: FILE_VALIDATION_MESSAGE,
      };
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const handleFocus = () => {
    setErrors({});
  };

  const handleMainButton = useCallback(async () => {
    if (!checkValid()) return;
    if (reportData.hasNoContract) return;

    setUploadStatus('loading');
    setUploadResult(null);

    try {
      const result = await reportData.handleOk();
      setUploadResult(result);
      setUploadStatus(result.uploaded > 0 ? 'success' : 'warning');
    } catch {
      setUploadStatus('error');
    }
  }, [reportData, errors]);

  const clearAndClose = useCallback(() => {
    reportData.handleReset();
    setErrors({});
    setUploadStatus('idle');
    setUploadResult(null);
    onCancel();
  }, [reportData, onCancel]);

  const handleRetry = useCallback(() => {
    reportData.handleReset();
    setErrors({});
    setUploadStatus('idle');
    setUploadResult(null);
  }, [reportData]);

  const handleDownloadTemplate = () => {
    if (!reportData.taskType) return;
    void reportData.handleDownloadTemplate();
  };

  const scenarioOptions = reportData.taskTypes.map((taskType) => ({
    value: taskType,
    label: reportData.taskTypeLabels[taskType] ?? taskType,
  }));

  const renderScenarioFields = () => {
    if (!reportData.taskType) return null;

    const showCampaignName =
      reportData.scenarioGroup === 'dispensarization' ||
      reportData.scenarioGroup === 'form';
    const showStartDate =
      reportData.scenarioGroup === 'dispensarization' ||
      reportData.scenarioGroup === 'form';
    const showEndDate = reportData.scenarioGroup === 'dispensarization';
    const showTemplate = reportData.scenarioGroup === 'form';

    return (
      <>
        {showCampaignName && (
          <Form.Item
            label={
              <span>
                Название кампании <span className={styles.requiredMark}>*</span>
              </span>
            }
            validateStatus={errors.name?.validateStatus}
            help={errors.name?.help}
          >
            <Input
              value={reportData.name}
              onChange={(event) => reportData.onNameChange(event.target.value)}
              onFocus={handleFocus}
              placeholder="Введите название кампании"
            />
          </Form.Item>
        )}

        {showStartDate && showEndDate ? (
          <div className={styles.dateTimeTwoColumn}>
            <Form.Item
              className={styles.dateTimeField}
              label={
                <span>
                  Дата/время начала
                  <span className={styles.requiredMark}> *</span>
                </span>
              }
              validateStatus={errors.fromDate?.validateStatus}
              help={errors.fromDate?.help}
            >
              <div className={styles.dateTimeRow}>
                <DatePicker
                  value={reportData.fromDate}
                  onChange={reportData.onFromDateChange}
                  onFocus={handleFocus}
                  format="DD.MM.YYYY"
                  disabledDate={isBeforeMinimumCampaignStartDate}
                  placeholder="дд.мм.гггг"
                />
                <TimePicker
                  value={reportData.fromTime}
                  onChange={reportData.onFromTimeChange}
                  format="HH:mm"
                  onFocus={handleFocus}
                  placeholder="--:--"
                />
              </div>
            </Form.Item>

            <Form.Item
              className={styles.dateTimeField}
              label={
                <span>
                  Дата/время окончания
                  <span className={styles.requiredMark}> *</span>
                </span>
              }
              validateStatus={errors.toDate?.validateStatus}
              help={errors.toDate?.help}
            >
              <div className={styles.dateTimeRow}>
                <DatePicker
                  value={reportData.toDate}
                  onChange={reportData.onToDateChange}
                  onFocus={handleFocus}
                  format="DD.MM.YYYY"
                  disabledDate={(date) =>
                    isBeforeCampaignEndDate(date, reportData.fromDate)
                  }
                  placeholder="дд.мм.гггг"
                />
                <TimePicker
                  value={reportData.toTime}
                  onChange={reportData.onToTimeChange}
                  format="HH:mm"
                  disabledTime={() =>
                    getCampaignEndDisabledTime(
                      reportData.toDate,
                      reportData.fromDate,
                      reportData.fromTime,
                    )
                  }
                  hideDisabledOptions
                  onFocus={handleFocus}
                  placeholder="--:--"
                />
              </div>
            </Form.Item>
          </div>
        ) : (
          showStartDate && (
            <Form.Item
              label={
                <span>
                  Дата/время начала
                  <span className={styles.requiredMark}> *</span>
                </span>
              }
              validateStatus={errors.fromDate?.validateStatus}
              help={errors.fromDate?.help}
            >
              <div className={styles.dateTimeRow}>
                <DatePicker
                  value={reportData.fromDate}
                  onChange={reportData.onFromDateChange}
                  onFocus={handleFocus}
                  format="DD.MM.YYYY"
                  disabledDate={isBeforeMinimumCampaignStartDate}
                  placeholder="дд.мм.гггг"
                />
                <TimePicker
                  value={reportData.fromTime}
                  onChange={reportData.onFromTimeChange}
                  format="HH:mm"
                  onFocus={handleFocus}
                  placeholder="--:--"
                />
              </div>
            </Form.Item>
          )
        )}

        {showTemplate && (
          <Form.Item
            label="Шаблон анкеты"
            validateStatus={errors.templateId?.validateStatus}
            help={errors.templateId?.help}
          >
            <Select
              value={reportData.templateId}
              onChange={reportData.onTemplateChange}
              onFocus={handleFocus}
              options={reportData.formTemplates.map((template) => ({
                value: template.id,
                label: template.name,
              }))}
              placeholder="Выберите шаблон анкеты"
            />
          </Form.Item>
        )}
      </>
    );
  };

  const renderFooter = () => {
    if (hasResult) {
      return (
        <div key="buttonsRow" className={styles.buttonsRow}>
          <Button key="retry" onClick={handleRetry}>
            Загрузить другой файл
          </Button>
          <Button key="close" type="primary" onClick={clearAndClose}>
            Закрыть
          </Button>
        </div>
      );
    }

    return (
      <div key="buttonsRow" className={styles.buttonsRow}>
        {reportData.taskType && (
          <Button
            key="download"
            type="primary"
            onClick={handleDownloadTemplate}
            loading={reportData.isTemplateLoading}
            disabled={
              isLoading ||
              reportData.isTemplateLoading ||
              reportData.hasNoContract
            }
          >
            <DownloadOutlined />
            &nbsp;Скачать шаблон
          </Button>
        )}
        <Button
          key="send"
          type="primary"
          onClick={handleMainButton}
          loading={isLoading}
          disabled={isLoading || reportData.hasNoContract}
        >
          Загрузить задания
        </Button>
      </div>
    );
  };

  return (
    <Modal
      className={styles.modalContent}
      open={open}
      onCancel={isLoading ? undefined : clearAndClose}
      closable={!isLoading}
      maskClosable={!isLoading}
      destroyOnHidden={true}
      footer={[renderFooter()]}
    >
      <p className={styles.titleModal}>Загрузка списка пациентов</p>

      <div className={styles.bodyWrapper}>
        {hasResult ? (
          <div className={styles.resultContainer}>
            <UploadResultBanner status={uploadStatus} result={uploadResult} />
          </div>
        ) : (
          <Spin
            spinning={isLoading || reportData.isTemplateLoading}
            tip={
              reportData.isTemplateLoading
                ? 'Скачивание шаблона...'
                : 'Загрузка файла...'
            }
          >
            <div
              className={
                isLoading || reportData.isTemplateLoading
                  ? styles.loadingOverlay
                  : undefined
              }
            >
              <Form layout="vertical" autoComplete="off">
                <Form.Item
                  label={
                    <span>
                      Сценарий <span className={styles.requiredMark}>*</span>
                    </span>
                  }
                  validateStatus={errors.taskType?.validateStatus}
                  help={errors.taskType?.help}
                >
                  <Select
                    value={reportData.taskType}
                    onChange={(value) => {
                      handleFocus();
                      reportData.onTaskTypeChange(value);
                    }}
                    options={scenarioOptions}
                    placeholder="Выберите сценарий"
                  />
                </Form.Item>

                {renderScenarioFields()}

                {reportData.hasNoContract && (
                  <Alert
                    className={styles.noContractAlert}
                    type="warning"
                    showIcon={true}
                    message={NO_CONTRACT_MESSAGE}
                  />
                )}
              </Form>

              <FormPopup
                fileList={reportData.fileList}
                onFileChange={reportData.onFileChange}
                errors={errors}
                handleFocus={handleFocus}
              />
            </div>
          </Spin>
        )}
      </div>
    </Modal>
  );
});

OutgoingCallsUploadPopup.displayName = 'OutgoingCallsUploadPopup';
export default OutgoingCallsUploadPopup;
